import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, 
  Paper, 
  Box, 
  Button, 
  ButtonGroup,
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import TableBarIcon from '@mui/icons-material/TableBar';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InventoryIcon from '@mui/icons-material/Inventory';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { useThemeContext } from '../../../contexts/ThemeContext';
import AddPackageScreenModal from './modals/AddPackageScreenModal';
import salesService, { PackageScreenDto } from '../../../services/salesService';
import QuickSaleScreenSettingsModal from '../quickSale/components/modals/QuickSaleScreenSettingsModal';
import CustomerImportModal from './modals/CustomerImportModal';
import useSalesScreensPageAgentBridge from '../hooks/useSalesScreensPageAgentBridge';
import BranchSearchFilterBar, { UNASSIGNED_BRANCH_ID } from '../../common/BranchSearchFilterBar';

// ✅ PackageScreenData - API'den gelen veri ile uyumlu
interface PackageScreenData {
  id: number;
  name: string;
  branchId?: string | null;
  branchName?: string;
  quickSaleScreenId: number;
  createdAt?: string;
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// ✅ Sayfa tema rengi - MUI palette için (ThemeContext.accent = info)
const PAGE_COLOR = 'info' as const;

const OrderManagementPage: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { colors: themeColors } = useThemeContext(); // ✅ ThemeContext'ten renkler
  const { getBranchName, branches, loadBranches } = useAuth();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [packageScreens, setPackageScreens] = useState<PackageScreenData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state'leri
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Raporlar flyout menüsü
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Satır işlemleri menüsü
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRowScreen, setSelectedRowScreen] = useState<PackageScreenData | null>(null);
  
  // ✅ YENİ: Ayarlar modalı state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsScreenId, setSettingsScreenId] = useState<string | null>(null);
  const [settingsScreenName, setSettingsScreenName] = useState<string>('');
  const [settingsPackageScreenId, setSettingsPackageScreenId] = useState<number | undefined>(undefined);
  const [settingsBranchId, setSettingsBranchId] = useState<string | null | undefined>(undefined);
  
  // ✅ YENİ: Düzenleme modalı state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editScreen, setEditScreen] = useState<PackageScreenData | null>(null);
  
  // Müşteri Aktar Modal state
  const [customerImportModalOpen, setCustomerImportModalOpen] = useState(false);

  // ✅ YENİ: Silme modalı state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [screenToDelete, setScreenToDelete] = useState<PackageScreenData | null>(null);
  const [deletingScreen, setDeletingScreen] = useState(false);

  // Şube + ekran adı filtresi
  const [filterBranchId, setFilterBranchId] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');

  const filteredScreens = useMemo(() => {
    const term = filterSearch.trim().toLocaleLowerCase('tr');
    return packageScreens.filter((screen) => {
      if (filterBranchId === UNASSIGNED_BRANCH_ID) {
        if (screen.branchId) return false;
      } else if (filterBranchId && screen.branchId !== filterBranchId) {
        return false;
      }
      if (!term) return true;
      return (screen.name || '').toLocaleLowerCase('tr').includes(term);
    });
  }, [packageScreens, filterBranchId, filterSearch]);

  
  const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, screen: PackageScreenData) => {
    setRowMenuAnchor(event.currentTarget);
    setSelectedRowScreen(screen);
  };
  
  const handleCloseRowMenu = () => {
    setRowMenuAnchor(null);
    setSelectedRowScreen(null);
  };
  
  // ✅ YENİ: Ayarlar modalını aç
  const handleOpenSettings = (screen: PackageScreenData) => {
    setSettingsScreenId(screen.quickSaleScreenId.toString());
    setSettingsScreenName(screen.name);
    setSettingsPackageScreenId(screen.id);
    setSettingsBranchId(screen.branchId);
    setSettingsModalOpen(true);
    handleCloseRowMenu();
  };
  
  // ✅ YENİ: Düzenleme modalını aç
  const handleOpenEditModal = (screen: PackageScreenData) => {
    setEditScreen(screen);
    setEditModalOpen(true);
    handleCloseRowMenu();
  };
  
  // ✅ YENİ: Silme modalını aç
  const handleOpenDeleteModal = (screen: PackageScreenData) => {
    setScreenToDelete(screen);
    setDeleteModalOpen(true);
    handleCloseRowMenu();
  };
  
  // ✅ YENİ: Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setScreenToDelete(null);
    setDeleteModalOpen(false);
  };
  
  // ✅ YENİ: Paket ekranını sil (soft delete)
  const handleDeleteScreen = async () => {
    if (!screenToDelete || !companyId) {
      setError(t('orderManagement.page.errors.deleteMissingInfo'));
      return;
    }
    
    setDeletingScreen(true);
    setError(null);
    
    try {
      // QuickSaleScreen'i sil → CASCADE ile PackageScreen otomatik silinir
      // Backend'de soft delete yapılıyor (IsDeleted = true)
      await salesService.deleteQuickSaleScreen(screenToDelete.quickSaleScreenId);
      
      console.log(`✅ Paket ekranı silindi: ${screenToDelete.name} (QuickSaleScreenId: ${screenToDelete.quickSaleScreenId})`);
      
      // Listeyi güncelle
      refreshPackageScreenList();
      
      handleCloseDeleteModal();
    } catch (err) {
      console.error("❌ Paket ekranı silinirken hata:", err);
      setError(t('orderManagement.page.errors.deleteFailed', { message: err instanceof Error ? err.message : t('orderManagement.platformMapping.errors.unknownError') }));
    } finally {
      setDeletingScreen(false);
    }
  };

  useEffect(() => {
    const uid = ownAuthSession.getCurrentUid();
    if (uid) {
      firebaseDataService.getUserData(uid).then((userData) => {
        if (userData) {
          setCompanyId(userData.companyId ?? null);
        } else {
          setError(t('orderManagement.page.errors.userCompanyNotFound'));
          setLoading(false);
        }
      }).catch((err) => {
        console.error("Şirket ID'si alınırken hata:", err);
        setError(t('orderManagement.page.errors.companyIdLoadFailed'));
        setLoading(false);
      });
    } else {
      setError(t('orderManagement.page.errors.userNotLoggedIn'));
      navigate('/login');
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    if (companyId && branches.length === 0) {
      loadBranches();
    }
  }, [companyId, branches.length, loadBranches]);

  useEffect(() => {
    if (!companyId) {
      if (loading) setLoading(false);
      return;
    }

    const fetchPackageScreens = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('📦 Paket ekranları yükleniyor...');
        const screens = await salesService.getPackageScreens();
        
        // API'den gelen veriyi local interface'e map et
        const mappedScreens: PackageScreenData[] = screens.map((s: PackageScreenDto) => ({
          id: s.Id,
          name: s.Name,
          branchId: s.BranchId,
          branchName: s.BranchId ? getBranchName(s.BranchId) : undefined,
          quickSaleScreenId: s.QuickSaleScreenId,
          createdAt: s.CreatedAt,
        }));
        
        setPackageScreens(mappedScreens);
        console.log(`✅ ${mappedScreens.length} paket ekranı yüklendi`);
      } catch (err: any) {
        console.error('Paket ekranları yüklenirken hata:', err);
        setError(t('orderManagement.page.errors.screensLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchPackageScreens();
  }, [companyId, branches, getBranchName, t]);

  const handleAddPackageScreen = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  // Paket ekranları listesini yenile
  const refreshPackageScreenList = async () => {
    if (!companyId) return;
    
    try {
      console.log('📦 Paket ekranları yenileniyor...');
      const screens = await salesService.getPackageScreens();
      
      // API'den gelen veriyi local interface'e map et
      const mappedScreens: PackageScreenData[] = screens.map((s: PackageScreenDto) => ({
        id: s.Id,
        name: s.Name,
        branchId: s.BranchId,
        branchName: s.BranchId ? getBranchName(s.BranchId) : undefined,
        quickSaleScreenId: s.QuickSaleScreenId,
        createdAt: s.CreatedAt,
      }));
      
      setPackageScreens(mappedScreens);
      console.log(`✅ ${mappedScreens.length} paket ekranı yenilendi`);
    } catch (err: any) {
      console.error('Paket ekranları yenilenirken hata:', err);
    }
  };

  const handleGoToScreen = (quickSaleScreenId: number) => {
    // ✅ QuickSaleScreenId kullanıyoruz çünkü PackageOrderScreen bu ID'yi bekliyor
    navigate(`/paket-ekrani/${quickSaleScreenId}`);
  };

  // AI ajan köprüsü: sayfa açıkken ajan paket ekranlarını listeleyebilir,
  // ayar modalını açabilir ve yeni ekran sonrası listeyi yenileyebilir.
  // (Erken return'lerden ÖNCE çağrılmalı — hook kuralları.)
  useSalesScreensPageAgentBridge('package', {
    screens: packageScreens.map((s) => ({
      id: String(s.id),
      name: s.name,
      branchName: s.branchName ?? null,
    })),
    openSettings: (screen) => {
      const full = packageScreens.find((s) => String(s.id) === screen.id);
      if (full) handleOpenSettings(full);
    },
    refreshScreens: () => refreshPackageScreenList(),
  });

  const pageLoading = loading && !companyId && !error;

  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
        <CircularProgress />
        <Typography sx={{ml: 2}}>{t('orderManagement.page.loadingCompany')}</Typography>
      </Box>
    );
  }
  
  return (
    <ScrollableContent>
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <LocalShippingIcon sx={{ fontSize: 32, mr: 1.5, color: themeColors.primary }} />
          <Typography variant="h5" component="h1">
            {t('orderManagement.page.title')}
          </Typography>
        </Box>
        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            sx={{ borderColor: themeColors.primary, color: themeColors.primary }}
            startIcon={<ListAltIcon />}
            onClick={() => navigate('/tum-siparisler')}
          >
            {t('orderManagement.page.actions.allOrders')}
          </Button>
          <ButtonGroup>
            <Button
              variant="contained"
              sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, opacity: 0.9 } }}
              startIcon={<AddIcon />}
              onClick={handleAddPackageScreen}
              disabled={!companyId}
            >
              {t('orderManagement.page.actions.addPackageScreen')}
            </Button>
            <Button
              variant="outlined"
              sx={{ borderColor: themeColors.primary, color: themeColors.primary, minWidth: 'auto', px: 1 }}
              onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </Button>
          </ButtonGroup>
          
          {/* 3 Nokta Menüsü */}
          <Menu
            anchorEl={reportsMenuAnchor}
            open={Boolean(reportsMenuAnchor)}
            onClose={() => setReportsMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { /* TODO: Sipariş Raporları */ setReportsMenuAnchor(null); }}>
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" sx={{ color: themeColors.primary }} />
              </ListItemIcon>
              <ListItemText>{t('orderManagement.page.menu.orderReports')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { navigate('/gun-sonu'); setReportsMenuAnchor(null); }}>
              <ListItemIcon>
                <AssessmentIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('orderManagement.page.menu.endOfDayReport')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { navigate('/gun-sonlari'); setReportsMenuAnchor(null); }}>
              <ListItemIcon>
                <HistoryIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>{t('orderManagement.page.menu.endOfDayList')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setCustomerImportModalOpen(true); setReportsMenuAnchor(null); }}>
              <ListItemIcon>
                <UploadFileIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>{t('orderManagement.page.menu.customerImport')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        
        {/* Mobil Butonlar */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, width: '100%', flexWrap: 'wrap' }}>
          <Button
            fullWidth
            variant="outlined"
            sx={{ borderColor: themeColors.primary, color: themeColors.primary }}
            startIcon={<ListAltIcon />}
            onClick={() => navigate('/tum-siparisler')}
            size="small"
          >
            {t('orderManagement.page.actions.allOrders')}
          </Button>
          <Button 
            fullWidth
            variant="contained" 
            sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, opacity: 0.9 } }}
            startIcon={<AddIcon />}
            onClick={handleAddPackageScreen}
            disabled={!companyId}
            size="small"
          >
            {t('orderManagement.page.actions.addPackageScreen')}
          </Button>
          <Button
            variant="outlined"
            sx={{ borderColor: themeColors.primary, color: themeColors.primary, minWidth: 'auto', px: 1 }}
            onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </Button>
        </Box>
      </HeaderContainer>

      {loading && companyId && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography sx={{ml: 2}}>{t('orderManagement.page.loadingScreens')}</Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!loading && !error && packageScreens.length === 0 && companyId && (
        <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            {t('orderManagement.page.emptyState.title')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('orderManagement.page.emptyState.subtitle')}
          </Typography>
          <Button
            variant="contained"
            color={PAGE_COLOR}
            startIcon={<AddIcon />}
            onClick={handleAddPackageScreen}
          >
            {t('orderManagement.page.emptyState.createFirst')}
          </Button>
        </Paper>
      )}

      {!loading && !error && packageScreens.length > 0 && (
        <BranchSearchFilterBar
          branches={branches}
          branchId={filterBranchId}
          onBranchChange={setFilterBranchId}
          search={filterSearch}
          onSearchChange={setFilterSearch}
          searchLabel={t('orderManagement.page.table.packageScreen')}
          shownCount={filteredScreens.length}
          totalCount={packageScreens.length}
        />
      )}

      {!loading && !error && packageScreens.length > 0 && filteredScreens.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {t('common2:listFilter.noResults')}
          </Typography>
        </Paper>
      )}

      {!loading && !error && filteredScreens.length > 0 && (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
            {filteredScreens.map((screen) => (
              <Paper 
                key={screen.id}
                elevation={2} 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { 
                    boxShadow: 4,
                    borderColor: themeColors.primary
                  }
                }}
              >
                {/* Ekran Adı */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <LocalShippingIcon sx={{ fontSize: 24, color: themeColors.primary }} />
                  <Typography variant="h6" fontWeight="bold">
                    {screen.name}
                  </Typography>
                </Box>

                {/* Şube Bilgisi */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">{t('orderManagement.page.table.branchLabel')}</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {screen.branchName || t('orderManagement.page.table.branchUnassigned')}
                  </Typography>
                </Box>

                {/* İşlem Butonları */}
                <Box display="flex" gap={1} alignItems="center">
                  <Button 
                    fullWidth
                    size="small" 
                    variant="outlined"
                    color={PAGE_COLOR}
                    onClick={() => handleGoToScreen(screen.quickSaleScreenId)}
                    startIcon={<InventoryIcon />}
                  >
                    {t('orderManagement.page.rowMenu.goToScreen')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenRowMenu(e, screen)}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* 🖥️ Desktop Table View (md+) */}
          <Paper elevation={3} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: themeColors.primary, color: 'white', width: '40%' }}>{t('orderManagement.page.table.packageScreen')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: themeColors.primary, color: 'white', width: '20%' }}>{t('orderManagement.page.table.branch')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: themeColors.primary, color: 'white', width: '40%', textAlign: 'right' }}>{t('orderManagement.page.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredScreens.map((screen) => (
                    <TableRow 
                      key={screen.id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle1" fontWeight="medium">{screen.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {screen.branchName || t('orderManagement.page.table.branchUnassigned')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ p: '6px' }}> 
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            color={PAGE_COLOR}
                            onClick={() => handleGoToScreen(screen.quickSaleScreenId)}
                            startIcon={<InventoryIcon />}
                          >
                            {t('orderManagement.page.rowMenu.goToScreen')}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenRowMenu(e, screen)}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Satır İşlemleri Menüsü */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedRowScreen) {
              handleOpenEditModal(selectedRowScreen);
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('orderManagement.page.rowMenu.edit')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedRowScreen) {
              handleOpenSettings(selectedRowScreen);
            }
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>{t('orderManagement.page.rowMenu.settings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            if (selectedRowScreen) {
              handleOpenDeleteModal(selectedRowScreen);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('orderManagement.page.rowMenu.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Paket Ekranı Ekleme Modalı */}
      {companyId && (
        <AddPackageScreenModal
          open={isAddModalOpen}
          onClose={handleCloseAddModal}
          companyId={companyId}
          onSaved={refreshPackageScreenList}
        />
      )}

      {/* ✅ YENİ: Paket Ekranı Ayarları Modalı */}
      <QuickSaleScreenSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        screenId={settingsScreenId}
        companyId={companyId}
        branchId={settingsBranchId}
        screenName={settingsScreenName}
        isPackageMode={true}
        packageScreenId={settingsPackageScreenId}
        onSaveSuccess={() => {
          // Ayarlar kaydedildiğinde listeyi yenile
          refreshPackageScreenList();
        }}
      />

      {/* ✅ YENİ: Paket Ekranı Düzenleme Modalı */}
      {companyId && editScreen && (
        <AddPackageScreenModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditScreen(null);
          }}
          companyId={companyId}
          editMode={true}
          packageScreenId={editScreen.id}
          quickSaleScreenId={editScreen.quickSaleScreenId}
          screenName={editScreen.name}
          screenBranchId={editScreen.branchId}
          onSaved={() => {
            refreshPackageScreenList();
            setEditModalOpen(false);
            setEditScreen(null);
          }}
        />
      )}

      {/* Müşteri Aktar Modalı */}
      <CustomerImportModal
        open={customerImportModalOpen}
        onClose={() => setCustomerImportModalOpen(false)}
      />

      {/* ✅ YENİ: Paket Ekranı Silme Onay Modalı */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t('orderManagement.page.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography
            dangerouslySetInnerHTML={{ __html: t('orderManagement.page.deleteDialog.description', { name: screenToDelete?.name ?? '' }) }}
          />
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            {t('orderManagement.page.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={deletingScreen}>
            {t('orderManagement.page.deleteDialog.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteScreen} 
            color="error" 
            variant="contained" 
            disabled={deletingScreen}
          >
            {deletingScreen ? <CircularProgress size={24} /> : t('orderManagement.page.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ScrollableContent>
  );
};

export default OrderManagementPage;
